import * as React from 'react';
import { useCallback } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Alert,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  FormHelperText,
  Unstable_Grid2 as Grid,
  InputLabel,
  OutlinedInput,
  Stack,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z as zod } from 'zod';

import type { User } from '@/types/v2/user';
import axios from '@/lib/v2/axios';

const schema = zod.object({
  first_name: zod.string().min(1, 'A first name is required'),
  last_name: zod.string().min(1, 'A last name is required'),
  email: zod.string().email('Invalid email address'),
  phone: zod
    .string()
    .min(1, 'A phone number is required')
    .regex(/^[\+]?[1-9][\d\s\-\(\)]{7,15}$|^\([1-9]\d{2}\)\s?\d{3}-\d{4}$/, 'Invalid phone number'), // More flexible phone validation
});

type Values = zod.infer<typeof schema>;

interface CreateOrEditResidentFormProps {
  onSuccess: (data: User) => void;
  initialData?: Values | User;
  mode: 'create' | 'edit';
  userId?: string | number;
  communityId?: string | number;
}

export function CreateOrEditResidentForm({
  onSuccess,
  initialData,
  userId,
  mode,
  communityId,
}: CreateOrEditResidentFormProps): React.JSX.Element {
  const [error, setError] = React.useState<string | null>(null);

  const defaultValues = {
    first_name: initialData?.first_name || '',
    last_name: initialData?.last_name || '',
    email: initialData?.email || '',
    phone: initialData?.phone || '',
  } satisfies Values;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<Values>({ defaultValues, resolver: zodResolver(schema) });

  const createUserRequest = useMutation({
    mutationFn: async (data: Values) => {
      if (mode === 'create') {
        await axios.get('/sanctum/csrf-cookie');
        return axios.post(`/api/v2/communities/${communityId}/residents`, data);
      }
      if (!userId) {
        console.error('User ID is required');
        setError('We encountered an issue while updating the user. Please try again later.');
        return;
      }

      console.log('Updating user', userId);
      await axios.get('/sanctum/csrf-cookie');
      return axios.put(`/api/v2/communities/${communityId}/residents/${userId}`, data);
    },
  });

  const handleCreateOrEditUser = useCallback(
    async (data: Values) => {
      console.log('data', data);
      try {
        const response = await createUserRequest.mutateAsync(data);
        onSuccess(response?.data);
        toast.success(`Success!`, {
          description: `${data.first_name} ${data.last_name} has been ${mode}ed successfully`,
          position: 'top-right',
          style: {
            background: '#107E4A',
            color: '#fff',
          },
        });
      } catch (error: any) {
        console.error(error);
        setError(error?.response?.data?.message || `An error occurred while ${mode}ing the user`);
      }
    },
    [createUserRequest] // Add selectedUnitTypes to dependencies
  );

  return (
    <Stack spacing={2}>
      {error ? <Alert severity="error">{error}</Alert> : null}
      <form onSubmit={handleSubmit(handleCreateOrEditUser)}>
        <Stack spacing={2}>
          <Card>
            <CardHeader title="User Details" />
            <CardContent>
              <Stack spacing={2}>
                <Grid container spacing={3}>
                  <Grid md={6} xs={12}>
                    <Controller
                      control={control}
                      name="first_name"
                      render={({ field }) => (
                        <FormControl error={Boolean(errors.first_name)} fullWidth>
                          <InputLabel required>First Name</InputLabel>
                          <OutlinedInput disabled={createUserRequest.isPending} {...field} />
                          {errors.first_name ? <FormHelperText>{errors.first_name.message}</FormHelperText> : null}
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid md={6} xs={12}>
                    <Controller
                      control={control}
                      name="last_name"
                      render={({ field }) => (
                        <FormControl error={Boolean(errors.last_name)} fullWidth>
                          <InputLabel required>Last Name</InputLabel>
                          <OutlinedInput disabled={createUserRequest.isPending} {...field} />
                          {errors.last_name ? <FormHelperText>{errors.last_name.message}</FormHelperText> : null}
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid md={6} xs={12}>
                    <Controller
                      control={control}
                      name="email"
                      render={({ field }) => (
                        <FormControl error={Boolean(errors.email)} fullWidth>
                          <InputLabel required>Email</InputLabel>
                          <OutlinedInput disabled={createUserRequest.isPending} {...field} />
                          {errors.email ? <FormHelperText>{errors.email.message}</FormHelperText> : null}
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid md={6} xs={12}>
                    <Controller
                      control={control}
                      name="phone"
                      render={({ field }) => (
                        <FormControl error={Boolean(errors.phone)} fullWidth>
                          <InputLabel required>Phone</InputLabel>
                          <OutlinedInput disabled={createUserRequest.isPending} {...field} />
                          {errors.phone ? <FormHelperText>{errors.phone.message}</FormHelperText> : null}
                        </FormControl>
                      )}
                    />
                  </Grid>
                </Grid>
              </Stack>
            </CardContent>
          </Card>

          <LoadingButton
            disabled={createUserRequest.isPending}
            loading={createUserRequest.isPending}
            type="submit"
            variant="contained"
          >
            {mode === 'create' ? 'Create Resident' : 'Edit Resident'}
          </LoadingButton>
        </Stack>
      </form>
    </Stack>
  );
}
